import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Turizm karbon hesaplamaları için export/import utilities
 */

/**
 * Hesaplamaları CSV formatında dışa aktarır
 * @param {Array} calculations - Dışa aktarılacak hesaplamalar
 * @param {string} filename - Dosya adı
 */
export const exportToCSV = (calculations, filename = 'karbon-hesaplamalari.csv') => {
  try {
    // CSV header'ları
    const headers = [
      'Tarih',
      'Başlık',
      'Otel Adı',
      'Periyot',
      'Mevsim',
      'Toplam Emisyon (kg CO2e)',
      'Oda-Gece Başına (kg CO2e)',
      'Konaklama',
      'Enerji',
      'Su',
      'Yemek & İçecek',
      'Çamaşır',
      'Atık',
      'Ulaşım',
      'Tesis Hizmetleri'
    ];

    // CSV satırları oluştur
    const csvRows = [headers];

    calculations.forEach(calc => {
      const row = [
        new Date(calc.timestamp).toLocaleDateString('tr-TR'),
        calc.title || '',
        calc.hotelName || '',
        getPeriodLabel(calc.period),
        getSeasonLabel(calc.season),
        Math.round(calc.total * 100) / 100,
        Math.round(calc.perRoomNight * 100) / 100,
        Math.round(calc.breakdown.accommodation * 100) / 100,
        Math.round(calc.breakdown.energy * 100) / 100,
        Math.round(calc.breakdown.water * 100) / 100,
        Math.round(calc.breakdown.food * 100) / 100,
        Math.round(calc.breakdown.laundry * 100) / 100,
        Math.round(calc.breakdown.waste * 100) / 100,
        Math.round(calc.breakdown.transportation * 100) / 100,
        Math.round(calc.breakdown.facilities * 100) / 100
      ];
      csvRows.push(row);
    });

    // CSV içeriği oluştur
    const csvContent = csvRows
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // BOM ekle (UTF-8 karakter desteği için)
    const BOM = '\uFEFF';
    const csvContentWithBOM = BOM + csvContent;

    // Dosyayı indir
    downloadFile(csvContentWithBOM, filename, 'text/csv;charset=utf-8;');
    
    return true;
  } catch (error) {
    console.error('CSV export error:', error);
    throw new Error('CSV dışa aktarma hatası: ' + error.message);
  }
};

/**
 * Hesaplamaları PDF formatında dışa aktarır
 * @param {Array} calculations - Dışa aktarılacak hesaplamalar
 * @param {string} filename - Dosya adı
 */
export const exportToPDF = (calculations, filename = 'karbon-hesaplamalari.pdf') => {
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Başlık
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('🌱 Otel & Turizm Karbon Ayak İzi Raporu', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 15;
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth / 2, yPosition, { align: 'center' });
    doc.text(`Toplam Hesaplama Sayısı: ${calculations.length}`, pageWidth / 2, yPosition + 5, { align: 'center' });

    yPosition += 20;

    // Her hesaplama için detaylı sayfa
    calculations.forEach((calc, index) => {
      if (index > 0) {
        doc.addPage();
        yPosition = 20;
      }

      // Hesaplama başlığı
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`${index + 1}. ${calc.title || 'Başlıksız Hesaplama'}`, 15, yPosition);
      
      yPosition += 10;

      // Genel bilgiler tablosu
      const generalInfo = [
        ['Otel Adı', calc.hotelName || 'Belirtilmemiş'],
        ['Tarih', new Date(calc.timestamp).toLocaleDateString('tr-TR')],
        ['Periyot', getPeriodLabel(calc.period)],
        ['Mevsim', getSeasonLabel(calc.season)],
        ['Toplam Emisyon', `${Math.round(calc.total)} kg CO₂e`],
        ['Oda-Gece Başına', `${Math.round(calc.perRoomNight)} kg CO₂e`]
      ];

      doc.autoTable({
        startY: yPosition,
        head: [['Özellik', 'Değer']],
        body: generalInfo,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [46, 125, 50] },
        margin: { left: 15, right: 15 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Kategori bazında emisyonlar
      const breakdownData = Object.entries(calc.breakdown)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => [
          getCategoryDisplayName(key),
          `${Math.round(value * 100) / 100} kg CO₂e`,
          `${((value / calc.total) * 100).toFixed(1)}%`
        ]);

      if (breakdownData.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Kategori Bazında Emisyon Dağılımı', 15, yPosition);
        yPosition += 5;

        doc.autoTable({
          startY: yPosition,
          head: [['Kategori', 'Emisyon', 'Yüzde']],
          body: breakdownData,
          theme: 'striped',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 150, 243] },
          margin: { left: 15, right: 15 }
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Öneriler
      if (calc.recommendations && calc.recommendations.length > 0) {
        // Sayfa kontrolü
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('İyileştirme Önerileri', 15, yPosition);
        yPosition += 5;

        const recommendationsData = calc.recommendations.map((rec, i) => [
          `${i + 1}`,
          rec.category,
          rec.priority,
          rec.suggestion,
          `${rec.potentialSaving} kg CO₂e`
        ]);

        doc.autoTable({
          startY: yPosition,
          head: [['#', 'Kategori', 'Öncelik', 'Öneri', 'Potansiyel Tasarruf']],
          body: recommendationsData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 3,
            overflow: 'linebreak'
          },
          headStyles: { fillColor: [255, 152, 0] },
          columnStyles: {
            3: { cellWidth: 'auto' }
          },
          margin: { left: 15, right: 15 }
        });
      }
    });

    // Özet sayfa ekle
    doc.addPage();
    yPosition = 20;

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('📊 Genel Özet ve İstatistikler', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    // İstatistikler hesapla
    const totalEmissions = calculations.reduce((sum, calc) => sum + calc.total, 0);
    const avgEmissions = totalEmissions / calculations.length;
    const maxEmission = Math.max(...calculations.map(calc => calc.total));
    const minEmission = Math.min(...calculations.map(calc => calc.total));

    const summaryData = [
      ['Toplam Hesaplama Sayısı', calculations.length.toString()],
      ['Toplam Emisyon', `${Math.round(totalEmissions)} kg CO₂e`],
      ['Ortalama Emisyon', `${Math.round(avgEmissions)} kg CO₂e`],
      ['En Yüksek Emisyon', `${Math.round(maxEmission)} kg CO₂e`],
      ['En Düşük Emisyon', `${Math.round(minEmission)} kg CO₂e`]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metrik', 'Değer']],
      body: summaryData,
      theme: 'grid',
      styles: { fontSize: 12 },
      headStyles: { fillColor: [76, 175, 80] },
      margin: { left: 15, right: 15 }
    });

    yPosition = doc.lastAutoTable.finalY + 20;

    // Kategori bazında ortalamalar
    const categoryAverages = {};
    Object.keys(calculations[0].breakdown).forEach(category => {
      const sum = calculations.reduce((sum, calc) => sum + calc.breakdown[category], 0);
      categoryAverages[category] = sum / calculations.length;
    });

    const categoryData = Object.entries(categoryAverages)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => [
        getCategoryDisplayName(key),
        `${Math.round(value * 100) / 100} kg CO₂e`
      ]);

    if (categoryData.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Kategori Bazında Ortalama Emisyonlar', 15, yPosition);
      yPosition += 5;

      doc.autoTable({
        startY: yPosition,
        head: [['Kategori', 'Ortalama Emisyon']],
        body: categoryData,
        theme: 'striped',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [156, 39, 176] },
        margin: { left: 15, right: 15 }
      });
    }

    // Footer ekle
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(
        `Sayfa ${i} / ${pageCount} - Otel & Turizm Karbon Ayak İzi Hesaplayıcısı`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // PDF'i indir
    doc.save(filename);
    
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw new Error('PDF dışa aktarma hatası: ' + error.message);
  }
};

/**
 * JSON formatında veri dışa aktarır (backup için)
 * @param {Array} calculations - Dışa aktarılacak hesaplamalar
 * @param {string} filename - Dosya adı
 */
export const exportToJSON = (calculations, filename = 'karbon-hesaplamalari-backup.json') => {
  try {
    const exportData = {
      exportDate: new Date().toISOString(),
      version: '1.0',
      calculations: calculations,
      metadata: {
        totalCalculations: calculations.length,
        dateRange: {
          from: Math.min(...calculations.map(calc => new Date(calc.timestamp).getTime())),
          to: Math.max(...calculations.map(calc => new Date(calc.timestamp).getTime()))
        }
      }
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    downloadFile(jsonContent, filename, 'application/json');
    
    return true;
  } catch (error) {
    console.error('JSON export error:', error);
    throw new Error('JSON dışa aktarma hatası: ' + error.message);
  }
};

/**
 * Excel formatında karşılaştırma raporu oluşturur
 * @param {Array} calculations - Karşılaştırılacak hesaplamalar
 * @param {string} filename - Dosya adı
 */
export const exportComparisonToCSV = (calculations, filename = 'karbon-karsilastirma.csv') => {
  try {
    if (calculations.length < 2) {
      throw new Error('Karşılaştırma için en az 2 hesaplama gerekli');
    }

    // Karşılaştırma tablosu header'ı
    const headers = ['Kategori'];
    calculations.forEach((calc, index) => {
      headers.push(`${calc.title || `Hesaplama ${index + 1}`} (kg CO₂e)`);
    });
    headers.push('En Yüksek', 'En Düşük', 'Ortalama');

    const csvRows = [headers];

    // Kategori bazında karşılaştırma
    const categories = Object.keys(calculations[0].breakdown);
    
    categories.forEach(category => {
      const row = [getCategoryDisplayName(category)];
      const values = calculations.map(calc => calc.breakdown[category] || 0);
      
      // Her hesaplama için değer
      values.forEach(value => {
        row.push(Math.round(value * 100) / 100);
      });
      
      // İstatistikler
      const max = Math.max(...values);
      const min = Math.min(...values);
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      
      row.push(
        Math.round(max * 100) / 100,
        Math.round(min * 100) / 100,
        Math.round(avg * 100) / 100
      );
      
      csvRows.push(row);
    });

    // Toplam satırı
    const totalRow = ['TOPLAM'];
    const totals = calculations.map(calc => calc.total);
    totals.forEach(total => {
      totalRow.push(Math.round(total * 100) / 100);
    });
    
    const maxTotal = Math.max(...totals);
    const minTotal = Math.min(...totals);
    const avgTotal = totals.reduce((sum, val) => sum + val, 0) / totals.length;
    
    totalRow.push(
      Math.round(maxTotal * 100) / 100,
      Math.round(minTotal * 100) / 100,
      Math.round(avgTotal * 100) / 100
    );
    
    csvRows.push(totalRow);

    // CSV içeriği oluştur
    const csvContent = csvRows
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // BOM ekle
    const BOM = '\uFEFF';
    const csvContentWithBOM = BOM + csvContent;

    downloadFile(csvContentWithBOM, filename, 'text/csv;charset=utf-8;');
    
    return true;
  } catch (error) {
    console.error('Comparison export error:', error);
    throw new Error('Karşılaştırma dışa aktarma hatası: ' + error.message);
  }
};

/**
 * Dosya indirme helper fonksiyonu
 * @param {string} content - Dosya içeriği
 * @param {string} filename - Dosya adı
 * @param {string} contentType - İçerik tipi
 */
const downloadFile = (content, filename, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Periyot etiketini döndürür
 * @param {string} period - Periyot
 * @returns {string} Türkçe etiket
 */
const getPeriodLabel = (period) => {
  const labels = { daily: 'Günlük', monthly: 'Aylık', yearly: 'Yıllık' };
  return labels[period] || period;
};

/**
 * Mevsim etiketini döndürür
 * @param {string} season - Mevsim
 * @returns {string} Türkçe etiket
 */
const getSeasonLabel = (season) => {
  const labels = { 
    spring: 'İlkbahar', 
    summer: 'Yaz', 
    autumn: 'Sonbahar', 
    winter: 'Kış' 
  };
  return labels[season] || season;
};

/**
 * Kategori görünen adını döndürür
 * @param {string} key - Kategori anahtarı
 * @returns {string} Türkçe kategori adı
 */
const getCategoryDisplayName = (key) => {
  const names = {
    accommodation: 'Konaklama',
    energy: 'Enerji',
    water: 'Su',
    food: 'Yemek & İçecek',
    laundry: 'Çamaşır',
    waste: 'Atık',
    transportation: 'Ulaşım',
    facilities: 'Tesis Hizmetleri'
  };
  return names[key] || key;
};
